/**
 * Condition d'exécution d'une comparaison de modèles (SPEC.md §3.3,
 * CLAUDE.md §2).
 *
 * C'est la contrainte la plus importante du produit, et la seule défense contre
 * le risque qui le tuerait : un comparateur circulaire, où la simulation
 * « démontre » ce qu'on lui a fait poser en hypothèse. La réponse est
 * mécanique — sans données d'observation externes, la comparaison ne
 * s'exécute pas.
 *
 * Ce n'est pas un avertissement qu'on peut écarter d'un clic. Il n'y a pas de
 * mode dégradé, pas de drapeau pour passer outre. Toute évolution qui en
 * ajouterait un est à refuser.
 */

import { coreFr } from "../i18n/core-fr.js";
import type { Dataset } from "./dataset.js";
import { columnOf } from "./dataset.js";

export interface ComparisonRequest {
  /** Identifiants des modèles mis en concurrence. */
  readonly models: readonly string[];
  /** Le dataset d'arbitrage. `undefined` est un cas prévu, pas une erreur. */
  readonly dataset: Dataset | undefined;
  /** Nom de la colonne sur laquelle porte la confrontation. */
  readonly observable: string;
}

export type Verdict =
  | { readonly ok: true }
  | { readonly ok: false; readonly reasons: readonly string[] };

export function checkComparability(request: ComparisonRequest): Verdict {
  const reasons: string[] = [];

  if (request.models.length < 2) {
    reasons.push(
      coreFr.comparability.tooFewModels,
    );
  }

  const { dataset } = request;

  if (dataset === undefined) {
    reasons.push(
      coreFr.comparability.missingDataset,
    );
    return { ok: false, reasons };
  }

  if (!dataset.isExternal) {
    reasons.push(
      coreFr.comparability.notExternal(dataset.name),
    );
  }

  const column = columnOf(dataset, request.observable);

  if (column === undefined) {
    reasons.push(
      coreFr.comparability.missingObservable(String(request.observable), String(dataset.columns
        .map((c) => c.name)
        .join(", "))),
    );
    return reasons.length > 0 ? { ok: false, reasons } : { ok: true };
  }

  if (column.unit === undefined) {
    reasons.push(
      coreFr.comparability.missingUnit(String(request.observable)),
    );
  }

  if (!column.hasUncertainty) {
    reasons.push(
      coreFr.comparability.missingUncertainty(String(request.observable)),
    );
  }

  return reasons.length > 0 ? { ok: false, reasons } : { ok: true };
}
